import * as React from 'react'
import { Button, Form } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Status } from 'api/common/statuses'
import { getWithdrawalHistoryAPI, WithdrawalHistoryRecord, WithdrawalHistoryResponse, WithdrawalStatus, WITHDRAWAL_STATUS_LABELS } from 'api/withdrawal'
import Footer from 'components/Footer'
import Header from 'components/Header'
import loginData from 'utilities/loginData'
import { numberFormat, dateFormatter } from 'utilities/typeUtility'
import { StatusBadge, StatusColors } from 'components/StatusBadge'

/**
 * 出金履歴画面
 */
const WithdrawalHistories: React.FC = () => {
  const navigate = useNavigate()
  const [historyData, setHistoryData] = React.useState<WithdrawalHistoryResponse | null>(null)
  const [selectedYear, setSelectedYear] = React.useState<string>(new Date().getFullYear().toString())
  const [selectedMonth, setSelectedMonth] = React.useState<string>('all')
  const [filteredRecords, setFilteredRecords] = React.useState<WithdrawalHistoryRecord[]>([])
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')

  const calcYearTotal = () => {
    if (!historyData) return 0
    return historyData.records
      .filter((record) => record.status === WithdrawalStatus.RequestAccepted && record.request_accepted_at && dateFormatter(record.request_accepted_at, 'yyyy') === selectedYear)
      .reduce((sum, record) => sum + record.withdrawal_amount, 0)
  }

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
      return
    }

    getWithdrawalHistoryAPI((response, status) => {
      if (status === Status.Success && response) {
        setHistoryData(response)
        const sorted = [...response.records].sort((a, b) => {
          const dateA = (a.status === WithdrawalStatus.RequestAccepted ? a.request_accepted_at : a.requested_at)?.getTime() ?? 0
          const dateB = (b.status === WithdrawalStatus.RequestAccepted ? b.request_accepted_at : b.requested_at)?.getTime() ?? 0
          return dateB - dateA
        })
        setFilteredRecords(sorted)
      }
    })
  }, [navigate])

  const sortRecords = (records: WithdrawalHistoryRecord[], order: 'asc' | 'desc') => {
    return [...records].sort((a, b) => {
      const dateA = (a.status === WithdrawalStatus.RequestAccepted ? a.request_accepted_at : a.requested_at)?.getTime() ?? 0
      const dateB = (b.status === WithdrawalStatus.RequestAccepted ? b.request_accepted_at : b.requested_at)?.getTime() ?? 0
      return order === 'desc' ? dateB - dateA : dateA - dateB
    })
  }

  const filterRecords = () => {
    if (!historyData) return

    let records = historyData.records.filter((record) => {
      const recordDate = record.status === WithdrawalStatus.RequestAccepted ? record.request_accepted_at : record.requested_at
      if (!recordDate) return false

      const recordYear = dateFormatter(recordDate, 'yyyy')
      if (recordYear !== selectedYear) return false

      if (selectedMonth !== 'all') {
        const recordMonth = dateFormatter(recordDate, 'MM')
        return recordMonth === selectedMonth
      }
      return true
    })

    setFilteredRecords(sortRecords(records, sortOrder))
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(e.target.value)
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(e.target.value)
  }

  React.useEffect(() => {
    filterRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, historyData])

  const handleDateSort = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc'
    setSortOrder(newOrder)
    setFilteredRecords(sortRecords(filteredRecords, newOrder))
  }

  const handleNewWithdrawal = () => {
    navigate('/showWithdrawal')
  }

  const getStatusColor = (status: WithdrawalStatus): StatusColors => {
    switch (status) {
      case WithdrawalStatus.InProgress:
        return {
          background: 'linear-gradient(90deg, #0dcaf0 0%, #0d6efd 100%)',
          color: 'white',
        }
      case WithdrawalStatus.RequestAccepted:
        return {
          background: 'linear-gradient(90deg, #9ece24 0%, #25c02e 100%)',
          color: 'white',
        }
      case WithdrawalStatus.Paused:
        return {
          background: 'linear-gradient(90deg, #6c757d 0%, #343a40 100%)',
          color: 'white',
        }
      default:
        return {
          background: '#6c757d',
          color: 'white',
        }
    }
  }

  // Generate year options for dropdown
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = 0; i < 4; i++) {
      years.push((currentYear - i).toString())
    }
    return years
  }

  // Generate month options for dropdown
  const generateMonthOptions = () => {
    return [
      { value: '01', label: '1月' },
      { value: '02', label: '2月' },
      { value: '03', label: '3月' },
      { value: '04', label: '4月' },
      { value: '05', label: '5月' },
      { value: '06', label: '6月' },
      { value: '07', label: '7月' },
      { value: '08', label: '8月' },
      { value: '09', label: '9月' },
      { value: '10', label: '10月' },
      { value: '11', label: '11月' },
      { value: '12', label: '12月' },
    ]
  }

  return (
    <>
      <Header title="アフィリエイトボーナス出金履歴" />
      <main>
        {/* Summary Section */}
        <section className="mx-4 mb-4" style={{ marginTop: '1.25rem' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              border: 'none',
              borderRadius: '5px',
              padding: '20px',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            <div style={{ color: '#102a7e', fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>{selectedYear}年 報酬累計金額</div>
            <div
              style={{
                color: '#102a7e',
                fontSize: '32px',
                fontWeight: 'bold',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'baseline',
              }}
            >
              {numberFormat(calcYearTotal())}
              <span style={{ fontSize: '18px', marginLeft: '4px', fontWeight: 'normal' }}>円</span>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="mx-4 mb-4">
          <div style={{ background: 'white', borderRadius: '5px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '8px' }}>期間選択</div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Form.Select
                value={selectedYear}
                onChange={handleYearChange}
                style={{
                  flex: 1,
                  border: '2px solid #e9ecef',
                  borderRadius: '5px',
                  padding: '12px',
                  fontSize: '16px',
                }}
              >
                {generateYearOptions().map((year) => (
                  <option key={year} value={year}>
                    {year}年
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                value={selectedMonth}
                onChange={handleMonthChange}
                style={{
                  flex: 1,
                  border: '2px solid #e9ecef',
                  borderRadius: '5px',
                  padding: '12px',
                  fontSize: '16px',
                }}
              >
                <option value="all">ALL</option>
                {generateMonthOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </section>

        {/* History Table Section */}
        <section className="mx-4 mb-5">
          {filteredRecords.length > 0 ? (
            <div style={{ background: 'white', borderRadius: '5px', overflow: 'hidden' }}>
              <div
                style={{
                  background: 'linear-gradient(90deg, #102a7e 0%, #0a75ba 100%)',
                  color: 'white',
                  padding: '16px 12px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '8px',
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={handleDateSort}
                >
                  日付 {sortOrder === 'desc' ? '↓' : '↑'}
                </div>
                <div style={{ textAlign: 'center' }}>出金金額</div>
                <div style={{ textAlign: 'center' }}>ステータス</div>
              </div>

              <div
                style={{
                  ...(filteredRecords.length > 6 && {
                    maxHeight: '384px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }),
                }}
              >
                {filteredRecords.map((record, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '16px 12px',
                      borderBottom: index < filteredRecords.length - 1 ? '1px solid #e9ecef' : 'none',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ fontSize: '14px', color: '#495057', fontWeight: '500', textAlign: 'center' }}>
                      {dateFormatter(record.request_accepted_at ? record.request_accepted_at : record.requested_at, 'yyyy/MM/dd')}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#102a7e', textAlign: 'right' }}>{numberFormat(record.withdrawal_amount)}</div>
                    <div style={{ textAlign: 'center' }}>
                      <StatusBadge status={record.status} label={WITHDRAWAL_STATUS_LABELS[record.status]} getStatusColor={getStatusColor} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                background: 'white',
                borderRadius: '5px',
                textAlign: 'center',
                padding: '60px 20px',
                color: '#8a8a8a',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📋</div>
              <div>選択した期間の出金申請履歴がありません</div>
            </div>
          )}
        </section>

        {/* Action Buttons */}
        <section className="bg-white px-4 py-5">
          <Button variant="primary" className="w-100 py-4 mb-3" onClick={handleNewWithdrawal}>
            新規出金申請
          </Button>
        </section>
      </main>
      <Footer />
    </>
  )
}

export default WithdrawalHistories
